import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FolderOpen, Loader2, Sparkles, Upload, AlertTriangle, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { openGedDoc } from '../paiementsCb/gedUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ged-dossiers/membre`;
const CATEGORIES = [['rib', 'RIB'], ['avis_insee', 'Avis INSEE'], ['autre', 'Autres documents']];
const ROLES = ['super_admin', 'admin', 'cooper', 'expert'];

const LigneDoc = ({ d, headers }) => (
  <li className="flex items-start gap-2 py-1.5" data-testid={`ged-membre-doc-${d.ged_id}`}>
    <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-700" />
    <button type="button" onClick={() => openGedDoc(d.ged_id, headers)} className="text-left text-xs text-sky-800 hover:underline">
      {d.doc_type_label || d.filename}
    </button>
    <span className="ml-auto shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{d.sous_dossier}</span>
    {(d.analyse_ia?.alertes || []).length > 0 && (
      <span title={d.analyse_ia.alertes.join('\n')} className="shrink-0 text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /></span>
    )}
  </li>
);

export const DocumentsMembreGED = ({ membreId, token, userRole }) => {
  const [docs, setDocs] = useState(null);
  const [cout, setCout] = useState(16);
  const [categorie, setCategorie] = useState('rib');
  const [busy, setBusy] = useState(false);
  const [derniere, setDerniere] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const charger = useCallback(() => {
    axios.get(`${API}/${membreId}/documents`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setDocs(r.data.documents); setCout(r.data.cout_credits); })
      .catch(() => setDocs([]));
  }, [membreId, token]);
  useEffect(() => { if (membreId && ROLES.includes(String(userRole || '').toLowerCase())) charger(); }, [membreId, userRole, charger]);
  if (!membreId || !ROLES.includes(String(userRole || '').toLowerCase())) return null;

  const envoyer = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('categorie', categorie);
    setBusy(true); setDerniere(null);
    try {
      const r = await axios.post(`${API}/${membreId}/televerser`, fd, { headers });
      setDerniere(r.data);
      toast.success(`Classé dans la GED : ${r.data.titre}${r.data.credits_debites ? ` (${r.data.credits_debites} crédits)` : ''}`);
      charger();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Téléversement impossible'); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-sky-100 bg-gradient-to-br from-sky-50/40 to-white overflow-hidden" data-testid="ged-membre-documents">
      <div className="px-4 py-2.5 bg-gradient-to-r from-sky-50 to-indigo-50/40 border-b border-sky-100 flex items-center gap-2">
        <FolderOpen className="h-4 w-4 text-sky-700" />
        <span className="text-xs font-bold text-sky-800 uppercase tracking-wider">Documents — GED du membre</span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700" data-testid="ged-membre-cout">
          <Sparkles className="h-3 w-3" /> Analyse IA : {cout ? `${cout} crédits` : 'gratuite'}
        </span>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {CATEGORIES.map(([v, l]) => (
            <button key={v} type="button" onClick={() => setCategorie(v)} data-testid={`ged-membre-cat-${v}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${categorie === v ? 'border-sky-600 bg-sky-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-sky-400'}`}>
              {l}
            </button>
          ))}
          <label className="ml-auto">
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden" disabled={busy}
              onChange={(e) => { envoyer(e.target.files?.[0]); e.target.value = ''; }} data-testid="ged-membre-input" />
            <span className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-800 ${busy ? 'pointer-events-none opacity-60' : ''}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {busy ? 'Analyse IA en cours…' : 'Téléverser'}
            </span>
          </label>
        </div>
        <p className="text-[11px] text-slate-500">PDF ou photo, 15 Mo max. L'IA identifie le document, lui donne un titre précis et le classe automatiquement dans la GED.</p>
        {derniere && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid="ged-membre-resultat">
            <p className="font-semibold">{derniere.titre} → {derniere.sous_dossier}</p>
            {derniere.analyse?.resume && <p className="mt-0.5">{derniere.analyse.resume}</p>}
            {(derniere.analyse?.alertes || []).map((a) => <p key={a} className="mt-0.5 text-amber-800">⚠ {a}</p>)}
          </div>
        )}
        {docs === null ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
          <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto" data-testid="ged-membre-liste">
            {docs.map((d) => <LigneDoc key={d.ged_id} d={d} headers={headers} />)}
            {!docs.length && <li className="py-2 text-xs text-slate-400">Aucun document pour l'instant.</li>}
          </ul>
        )}
        {docs?.length > 0 && <Button variant="ghost" size="sm" onClick={charger} className="h-7 px-2 text-xs" data-testid="ged-membre-actualiser">Actualiser</Button>}
      </div>
    </div>
  );
};
